import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import Avatar from "@mui/material/Avatar";
import { Card } from "@mui/material";

import Table from "examples/Tables/Table";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import HotelSelector from "components/HotelSelector";
import NoResults from "components/NoResult";
import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";

import { FaArrowLeft, FaRedoAlt } from "react-icons/fa";

import useCommon from "hooks/useCommon";
import { getRemovedGuestDeletionJobs } from "store/slice/guestSlice";
import { retryGuestDeletionJob, resetRetryGuestDeletionState } from "store/slice/guestSlice";

dayjs.extend(relativeTime);
const { primary } = colors;

const GuestCell = ({ guest }) => {
  const initials =
    guest.name
      ?.split(" ")
      ?.map((p) => p[0])
      ?.join("")
      ?.toUpperCase() || "G";

  return (
    <VuiBox display="flex" alignItems="center" gap={1.25}>
      <Avatar
        sx={{
          width: 32,
          height: 32,
          fontSize: 12,
          color: primary.main,
          border: `1px solid ${rgba(primary.main, 0.25)}`,
          background: rgba(primary.main, 0.08),
        }}
      >
        {initials}
      </Avatar>

      <VuiBox display="flex" flexDirection="column" lineHeight={1.2}>
        <VuiTypography variant="body2" color="primary">{guest.name}</VuiTypography>

        <VuiTypography variant="caption" color="primary">
          {guest.email || ""}
        </VuiTypography>
      </VuiBox>
    </VuiBox>
  );
};

const TextCell = ({ value }) => <VuiTypography variant="caption" color="primary">{value || "—"}</VuiTypography>;

const DateCell = ({ date }) => {
  if (!date) return <TextCell value="—" />;

  return (
    <VuiBox display="flex" flexDirection="column" lineHeight={1.2}>
      <VuiTypography variant="caption" color="primary">{dayjs(date).format("MMM D, YYYY")}</VuiTypography>

      <VuiTypography variant="caption" color="primary">{dayjs(date).fromNow()}</VuiTypography>
    </VuiBox>
  );
};

const StatusCell = ({ status }) => {
  const config = {
    pending: { color: "#f59e0b", label: "Pending" },
    in_progress: { color: "#3b82f6", label: "In Progress" },
    partial: { color: "#f59e0b", label: "Partial" },
    failed: { color: "#ef4444", label: "Failed" },
  };

  const item = config[status] || config.pending;

  return (
    <VuiBox
      px={1.2}
      py={0.45}
      borderRadius="10px"
      sx={{
        background: `${item.color}20`,
        border: `1px solid ${item.color}55`,
        display: "inline-flex",
      }}
    >
      <VuiTypography variant="caption" sx={{ color: item.color, fontWeight: 500, fontSize: 11 }}>
        {item.label}
      </VuiTypography>
    </VuiBox>
  );
};

const ProgressCell = ({ progress }) => (
  <VuiTypography variant="caption" color="primary">
    {progress?.completedSteps || 0}/{progress?.totalSteps || 0}
  </VuiTypography>
);

const RetryCell = ({ disabled, loading, onClick }) => (
  <VuiButton
    size="small"
    variant="outlined"
    color="primary"
    disabled={disabled}
    onClick={onClick}
    startIcon={!loading ? <FaRedoAlt size={10} /> : null}
    sx={{ minWidth: 110 }}
  >
    {loading ? "Retrying..." : disabled ? "Processing" : "Retry"}
  </VuiButton>
);

const dummyRemovedGuests = [
  {
    jobId: "j1",
    guestId: "g1",
    guestSnapshot: {
      name: "John Doe",
      email: "john@email.com",
      phone: "+1 202 555 0101",
    },
    status: "failed",
    progress: { completedSteps: 3, totalSteps: 7 },
    deletedBy: { userName: "Super Admin" },
    startedAt: "2026-04-28T08:27:08.803Z",
    updatedAt: "2026-04-28T08:29:08.803Z",
  },
  {
    jobId: "j2",
    guestId: "g2",
    guestSnapshot: {
      name: "Emily Carter",
      email: "emily@email.com",
      phone: "+1 202 555 0102",
    },
    status: "partial",
    progress: { completedSteps: 5, totalSteps: 7 },
    deletedBy: { userName: "Manager" },
    startedAt: "2026-04-28T07:27:08.803Z",
    updatedAt: "2026-04-28T07:45:08.803Z",
  },
  {
    jobId: "j3",
    guestId: "g3",
    guestSnapshot: {
      name: "Michael Brown",
      email: "michael@email.com",
      phone: "+1 202 555 0103",
    },
    status: "in_progress",
    progress: { completedSteps: 2, totalSteps: 7 },
    deletedBy: { userName: "Ops Admin" },
    startedAt: "2026-04-28T09:00:08.803Z",
    updatedAt: "2026-04-28T09:02:08.803Z",
  },
];

const RemovedGuests = () => {
  const { DISPATCH, STORE, NAVIGATE } = useCommon();

  const hotelIds = STORE((state) => state.ui.hotelIds);

  const {
    removedGuests,
    removedGuestsLoading,
    removedGuestsTotal,
    retryGuestDeletionLoading,
    retryGuestDeletionJobId,
  } = STORE((state) => state.guests);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = search?.trim() || "";

      setDebouncedSearch(trimmed);
      setPage(0);
    }, 400);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!hotelIds?.length) return;

    DISPATCH(
      getRemovedGuestDeletionJobs({
        hotelIds,
        page: page + 1,
        pageSize: rowsPerPage,
        search: debouncedSearch,
      })
    );
  }, [hotelIds, page, rowsPerPage, debouncedSearch, DISPATCH]);

  const handleRetryGuestDeletion = async (job) => {
    try {
      await DISPATCH(retryGuestDeletionJob(job.jobId)).unwrap();

      DISPATCH(resetRetryGuestDeletionState());
    } catch (error) {
      console.log(error);
    }
  };

  const tableData = useMemo(
    () => ({
      columns: [
        { name: "guest", align: "left" },
        { name: "status", align: "center" },
        { name: "progress", align: "center" },
        { name: "started", align: "center" },
        { name: "updated", align: "center" },
        { name: "actions", align: "center" },
      ],

      rows: (removedGuests || []).map((g) => {
        const guest = {
          id: g.guestId,
          name: g.guestSnapshot?.name,
          email: g.guestSnapshot?.email,
          phone: g.guestSnapshot?.phone,
        };

        const retryDisabled =
          g.status === "pending" ||
          g.status === "in_progress" ||
          (retryGuestDeletionLoading && retryGuestDeletionJobId === g.jobId);

        return {
          guest: <GuestCell guest={guest} />,
          status: <StatusCell status={g.status} />,
          progress: <ProgressCell progress={g.progress} />,
          started: <DateCell date={g.startedAt} />,
          updated: <DateCell date={g.updatedAt} />,

          actions: (
            <RetryCell
              disabled={retryDisabled}
              loading={retryGuestDeletionLoading && retryGuestDeletionJobId === g.jobId}
              onClick={() => handleRetryGuestDeletion(g)}
            />
          ),
        };
      }),
    }),
    [removedGuests]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <VuiBox py={2}>
        <VuiBox mb={3}>
          <Card sx={{
            p: 2,
            color: primary.main,
            "& th, & th .MuiTypography-root, & .MuiTablePagination-displayedRows": {
              color: primary.main,
            },
            "& .MuiTablePagination-root .MuiIconButton-root:not(.Mui-disabled)": {
              color: primary.main,
            },
          }}>
            <VuiBox mb="22px">
              <VuiBox
                display={{ xs: "none", md: "flex" }}
                gap={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <VuiBox display="flex" alignItems="center" gap={1.5}>
                  <VuiButton
                    size="small"
                    color="primary"
                    onClick={() => NAVIGATE("/guestLibrary")}
                    startIcon={<FaArrowLeft size={12} />}
                    sx={{
                      minWidth: "unset",
                      px: 2,
                      py: 1,
                      border: "none",
                      boxShadow: "none",
                      background: "transparent",
                      color: primary.main,
                      "&:hover": {
                        background: rgba(primary.main, 0.06),
                        border: "none",
                        boxShadow: "none",
                      },
                      "&:focus": {
                        boxShadow: "none",
                      },
                    }}
                  >
                    Back
                  </VuiButton>
                </VuiBox>

                <VuiBox display="flex" gap={2} alignItems="center">
                  <HotelSelector />

                  <VuiBox sx={{ width: "240px" }}>
                    <VuiInput
                      gradient
                      fullWidth
                      placeholder="Search deletion jobs..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </VuiBox>
                </VuiBox>
              </VuiBox>
            </VuiBox>

            {!removedGuestsLoading && (removedGuests || []).length === 0 ? (
              <NoResults
                title="No Pending Deletion Jobs"
                description="Failed or partial guest deletions will appear here."
              />
            ) : (
              <Table
                columns={tableData.columns}
                rows={tableData.rows}
                pagination
                loading={removedGuestsLoading}
                paginationMode="server"
                page={page}
                rowsPerPage={rowsPerPage}
                total={removedGuestsTotal}
                onPageChange={(newPage) => setPage(newPage)}
                onRowsPerPageChange={(newLimit) => {
                  setRowsPerPage(newLimit);
                  setPage(0);
                }}
              />
            )}
          </Card>
        </VuiBox>
      </VuiBox>
    </DashboardLayout>
  );
};

export default RemovedGuests;
